import React, { useMemo } from 'react'
import {
  ContentHeader,
  ContentTitle,
} from 'components/Content'
import Content from '../../Content'
import ElementContentIcon from '../../ContentIcon'
import KanBoIcon from 'components/KanBoIcon'
import { FormattedMessage } from 'react-intl'
import useCardBlockers from './useCardBlockers'
import BlockerIssue from './BlockerIssue'
import createCardDateConflictsSelector from '../../selectors/createCardDateConflictsSelector'
import createIsCardOverdueSelector from 'routes/Board/selectors/createIsCardOverdueSelector'
import { useSelector } from 'react-redux'
import { useCardContext } from 'dataProviders/Card'
import { useBoardContext } from 'dataProviders/Board'
import Issue from './Issue'
import moment from 'moment'
import RelativeDate from 'components/RelativeDate'
import { ModalBoardContext, ModalCardContext } from 'components/Modal'
import DateConflicts from '../DateConflicts'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import CardName from 'components/CardName'
import { CardLink } from 'components/Link'
import styled, { css } from 'styled-components'
import createOverdueChildCardsSelector from 'routes/Board/selectors/createOverdueChildCardsSelector'
import createOverduePreviousCardsSelector from 'routes/Board/selectors/createOverduePreviousCardsSelector'

const StyledContent = styled(Content)`
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border-radius: inherit;

    ${props => {
      switch (props.issueLevel) {
        case 'danger':
          return css`
            box-shadow: inset 0 0 2px 2px #f55a4e;
          `
        case 'warning':
          return css`
            box-shadow: inset 0 0 2px 2px #fb8c00;
          `
      }
    }}
  }
`

const TypedRelativeDate: any = RelativeDate

const Issues: React.FC<LeftSidebarProps> = ({
  leftSidebar,
}) => {
  const cardContext = useCardContext()

  const boardContext = useBoardContext()

  const cardBlockers = useCardBlockers()

  const isCardOverdueSelector = useMemo(() => createIsCardOverdueSelector(), [])

  const isCardOverdue = useSelector<any, boolean>(state => isCardOverdueSelector({
    isArchived: state['view-board'].cards[cardContext.id]?.isArchived,
    isTemplate: state['view-board'].cards[cardContext.id]?.isTemplate,
    dueDate: state['view-board'].cards[cardContext.id]?.dueDate,
    statusId: state['view-board'].cards[cardContext.id]?.statusId,
    isBoardClosed: state['view-board'].view[state['view-board'].cards[cardContext.id]?.boardId]?.isClosed,
    cardStatusesById: state['view-board'].cardStatuses.byId,
  }))
  
  const startDate = useSelector<any, number>(state => state['view-board'].cards[cardContext.id]?.startDate)

  const dueDate = useSelector<any, number>(state => state['view-board'].cards[cardContext.id]?.dueDate)
  
  const conflictsSelector = useMemo(() => createCardDateConflictsSelector(), [])

  const startDateConflicts = useSelector<any, any>(state => conflictsSelector(state, {
    boardId: boardContext.id,
    cardId: cardContext.id,
    startDate,
  }))

  const dueDateConflicts = useSelector<any, any>(state => conflictsSelector(state, {
    boardId: boardContext.id,
    cardId: cardContext.id,
    dueDate,
  }))

  const previousBlockedCards = useSelector<any, number[] | undefined>(state => state.kanbo.previousBlockedCards[cardContext.id])

  const descendantBlockedCards = useSelector<any, number[] | undefined>(state => state.kanbo.descendantBlockedCards[cardContext.id])

  const overduePreviousCardsSelector = useMemo(() => createOverduePreviousCardsSelector(), [])

  const overduePreviousCards = useSelector<any, any[]>(state => overduePreviousCardsSelector({
    cardId: cardContext.id,
    cardRelations: state.kanbo.cardRelations.byId[cardContext.id],
    previousCardsDates: state['view-board'].cards[cardContext.id]?.previousCardsDates,
    cardStatusesById: state['view-board'].cardStatuses.byId,
    boardsById: state['view-board'].view,
    cardsById: state['view-board'].cards,   
  }))

  const overdueChildCardsSelector = useMemo(() => createOverdueChildCardsSelector(), [])

  const overdueChildCards = useSelector<any, any[]>(state => overdueChildCardsSelector({
    cardId: cardContext.id,
    cardRelations: state.kanbo.cardRelations.byId[cardContext.id],
    descendantCardsDates: state['view-board'].cards[cardContext.id]?.descendantCardsDates,
    cardStatusesById: state['view-board'].cardStatuses.byId,
    boardsById: state['view-board'].view,
    cardsById: state['view-board'].cards,    
  }))

  const [issueLevel, issueCount]: [IssueLevel | undefined, number] = useMemo(() => {
    let issueLevel: IssueLevel | undefined
    let issueCount = 0

    if (startDateConflicts.length > 0) {
      issueLevel = 'warning'
      issueCount += 1
    }

    if (dueDateConflicts.length > 0) {
      issueLevel = 'warning'
      issueCount += 1
    }

    if (overduePreviousCards.length > 0) {
      issueLevel = 'warning'
      issueCount += overduePreviousCards.length
    }

    if (overdueChildCards.length > 0) {
      issueLevel = 'warning'
      issueCount += overdueChildCards.length
    }

    if (previousBlockedCards?.length) {
      issueLevel = 'warning'
      issueCount += previousBlockedCards.length
    }

    if (descendantBlockedCards?.length) {
      issueLevel = 'warning'
      issueCount += descendantBlockedCards.length
    }

    if (isCardOverdue) {
      issueLevel = 'warning'
      issueCount += 1
    }

    if (cardBlockers.length > 0) {
      issueLevel = 'danger'
      issueCount += cardBlockers.length
    }

    return [issueLevel, issueCount]
  }, [
    startDateConflicts,
    dueDateConflicts,
    overduePreviousCards,
    overdueChildCards,
    previousBlockedCards,
    descendantBlockedCards,
    isCardOverdue,
    cardBlockers,
  ])

  const issueLevelClass: string | undefined = useMemo(() => {
    switch (issueLevel) {
      case 'danger':
        return 'text-danger'
      case 'warning':
        return 'text-warning'
      default:
        return undefined
    }
  }, [issueLevel])

  return issueCount > 0 ? (
    <StyledContent
      issueLevel={issueLevel}
    >
      <ContentHeader>
        <ElementContentIcon>
          <KanBoIcon className={issueLevelClass} name='kanbo-16-exclamation' />
        </ElementContentIcon>
        <ContentTitle className={issueLevelClass}>
          <FormattedMessage id='CARD_ISSUES_COUNT' values={{ count: issueCount }} />
        </ContentTitle>
      </ContentHeader>
      <div>
        {cardBlockers.map(cardBlocker =>
          <BlockerIssue
            key={cardBlocker.id}
            {...cardBlocker}
          />
        )}
        {isCardOverdue &&
          <Issue
            level='warning'
            icon={<KanBoIcon name='kanbo-16-due-date' />}
            title={<FormattedMessage id='CARD_ISSUES_OVERDUE' />}
          >
            <FormattedMessage
              id='DATE_FORMAT_FULL'
              values={{
                date: moment(dueDate).format('LLLL'),
                week: moment(dueDate).format('w'),
              }}
            >
              {formattedMessage =>
                <div title={formattedMessage as string}>
                  {moment(dueDate).format('D MMM LT')} (<TypedRelativeDate timestamp={dueDate} />)
                </div>
              }
            </FormattedMessage>
          </Issue>
        }
        {startDateConflicts.length > 0 &&
          <Issue
            level='warning'
            icon={<KanBoIcon name='kanbo-16-start-date' />}
            title={<FormattedMessage id='CARD_DATE_CONFLICTS_START_DATE' values={{ count: startDateConflicts.length }} />}
            onClick={() => {
              leftSidebar.push(() =>
                <ModalBoardContext boardId={boardContext.id}>
                  <ModalCardContext cardId={cardContext.id}>
                    <DateConflicts
                      errorCards={startDateConflicts}
                      title={
                        <FormattedMessage id='CARD_DATE_CONFLICTS_START_DATE' values={{ count: startDateConflicts.length }} />
                      }
                    />
                  </ModalCardContext>
                </ModalBoardContext>
              )
            }}
          >
            <FormattedMessage
              id='DATE_FORMAT_FULL'
              values={{
                date: moment(startDate).format('LLLL'),
                week: moment(startDate).format('w'),
              }}
            >
              {formattedMessage =>
                <div title={formattedMessage as string}>
                  {moment(startDate).format('D MMM LT')} (<TypedRelativeDate timestamp={startDate} />)
                </div>
              }
            </FormattedMessage>
          </Issue>
        }
        {dueDateConflicts.length > 0 &&
          <Issue
            level='warning'
            icon={<KanBoIcon name='kanbo-16-due-date' />}
            title={<FormattedMessage id='CARD_DATE_CONFLICTS_DUE_DATE' values={{ count: dueDateConflicts.length }} />}
            onClick={() => {
              leftSidebar.push(() =>
                <ModalBoardContext boardId={boardContext.id}>
                  <ModalCardContext cardId={cardContext.id}>
                    <DateConflicts
                      errorCards={dueDateConflicts}
                      title={
                        <FormattedMessage id='CARD_DATE_CONFLICTS_DUE_DATE' values={{ count: dueDateConflicts.length }} />
                      }
                    />
                  </ModalCardContext>
                </ModalBoardContext>
              )
            }}
          >
            <FormattedMessage
              id='DATE_FORMAT_FULL'
              values={{
                date: moment(dueDate).format('LLLL'),
                week: moment(dueDate).format('w'),
              }}
            >
              {formattedMessage =>
                <div title={formattedMessage as string}>
                  {moment(dueDate).format('D MMM LT')} (<TypedRelativeDate timestamp={dueDate} />)
                </div>
              }
            </FormattedMessage>
          </Issue>
        }
        {previousBlockedCards?.map(id =>
          <CardLink
            key={id}
            cardId={id}
            className='clear-link-style'
          >
            <Issue
              level='warning'
              icon={<KanBoIcon name='kanbo-16-block' />}
              title={<FormattedMessage id='BLOCKERS_PREVIOUS_CARD_BLOCKED' />}
              onClick={() => {}}
            >
              <CardName
                id={id}
              />
            </Issue>
          </CardLink>
        )}
        {descendantBlockedCards?.map(id =>
          <CardLink
            key={id}
            cardId={id}
            className='clear-link-style'
          >
            <Issue
              level='warning'
              icon={<KanBoIcon name='kanbo-16-block' />}
              title={<FormattedMessage id='BLOCKERS_DESCENDANT_CARD_BLOCKED' />}
              onClick={() => {}}
            >
              <CardName
                id={id}
              />
            </Issue>
          </CardLink>
        )}
        {overduePreviousCards.map(id =>
          <CardLink
            key={id}
            cardId={id}
            className='clear-link-style'
          >
            <Issue
              level='warning'
              icon={<KanBoIcon name='kanbo-16-due-date' />}
              title={<FormattedMessage id='CARD_ISSUES_PREVIOUS_CARD_OVERDUE' />}
              onClick={() => {}}
            >
              <CardName
                id={id}
              />
            </Issue>
          </CardLink>
        )}
        {overdueChildCards.map(id =>
          <CardLink
            key={id}
            cardId={id}
            className='clear-link-style'
          >
            <Issue
              level='warning'
              icon={<KanBoIcon name='kanbo-16-due-date' />}
              title={<FormattedMessage id='CARD_ISSUES_CHILD_CARD_OVERDUE' />}
              onClick={() => {}}
            >
              <CardName
                id={id}
              />
            </Issue>
          </CardLink>
        )}
      </div>
    </StyledContent>
  ) : null
}

export default withLeftSidebar(Issues)
